"use client";

import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FaClock, FaUsers, FaGraduationCap, FaChalkboardTeacher, 
  FaCertificate, FaLaptopCode, FaRocket, FaChevronDown, 
  FaChevronUp, FaDownload, FaPhone, FaEnvelope, FaStar, 
  FaCheckCircle, FaArrowRight, FaPlayCircle, FaCheck
} from "react-icons/fa";
import { IoLogoWhatsapp } from "react-icons/io";
import { HiOutlineBadgeCheck } from "react-icons/hi";
import axios from "axios";
import { prodServerUrl } from "../../../app/utils/globals";
import toast, { Toaster } from "react-hot-toast";
import CustomHead from "@/utils/CustomHead";
import SyllabusPopup from "@/components/SyllabusPopup";

// --- Animation Variants ---
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const CourseDetailTemplate = ({ courseData }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [activeSyllabus, setActiveSyllabus] = useState(0);
  const [activeFaq, setActiveFaq] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  
  // Form State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+91',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Refs
  const sectionRefs = {
    overview: useRef(null),
    syllabus: useRef(null),
    certification: useRef(null),
    instructors: useRef(null),
    faq: useRef(null),
  };

  // Sticky Nav Logic
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 600); // Activate after hero
      
      const scrollPosition = window.scrollY + 180;
      for (const key in sectionRefs) {
        const element = sectionRefs[key].current;
        if (element && element.offsetTop <= scrollPosition && (element.offsetTop + element.offsetHeight) > scrollPosition) {
          setActiveTab(key);
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (key) => {
    const element = sectionRefs[key].current;
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 140,
        behavior: "smooth"
      });
      setActiveTab(key);
    }
  };

  // Form Handlers
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setFormData(prev => ({ ...prev, phone: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${prodServerUrl}/course-lead-forms`, {
        courseName: courseData.title,
        ...formData
      });
      toast.success("Inquiry submitted successfully!");
      setFormData({ name: '', email: '', phone: '', countryCode: '+91', message: '' });
    } catch (error) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleSyllabus = (idx) => setActiveSyllabus(activeSyllabus === idx ? null : idx);
  const toggleFaq = (idx) => setActiveFaq(activeFaq === idx ? null : idx);

  // Data Fallbacks
  const syllabusData = courseData.syllabus || [
    { title: "Module 1: Foundations", description: "Introduction to core concepts.", topics: ["Setup", "Basics", "First Steps"] },
    { title: "Module 2: Advanced Techniques", description: "Deep dive into complex topics.", topics: ["Advanced Logic", "Optimization", "Security"] },
  ];

  return (
    <>
      <CustomHead title={courseData.title} />
      <Toaster position="top-center" />

      <div className="bg-slate-50 min-h-screen font-sans text-slate-800">
        
        {/* --- Hero Section with Form --- */}
        <section className="relative bg-slate-900 pt-28 pb-20 lg:pt-32 lg:pb-24 overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 opacity-95"></div>
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-blue-600/10 rounded-full blur-[100px] pointer-events-none"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start">
              
              {/* Left Column: Course Info */}
              <motion.div 
                initial="hidden" animate="visible" variants={fadeInUp}
                className="lg:col-span-7 text-white space-y-6 pt-4"
              >
                {/* Badges */}
                <div className="flex flex-wrap gap-3">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-300 border border-yellow-500/30 text-xs font-bold uppercase tracking-wide">
                    <FaStar className="mb-0.5" /> Premium Course
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-500/20 text-green-300 border border-green-500/30 text-xs font-bold uppercase tracking-wide">
                    <HiOutlineBadgeCheck className="text-lg" /> Certified
                  </span>
                </div>

                <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight text-white">
                  {courseData.title}
                </h1>
                
                <p className="text-lg text-slate-300 leading-relaxed max-w-2xl">
                  {courseData.description || "Master the skills needed to excel in this field with our comprehensive course designed by industry experts."}
                </p>

                {/* Key Stats Row */}
                <div className="flex flex-wrap gap-6 py-4 border-t border-white/10 mt-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center text-blue-400">
                      <FaClock size={20} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 uppercase font-bold">Duration</p>
                      <p className="font-semibold text-yellow">{courseData.duration || "Self Paced"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center text-purple-400">
                      <FaUsers size={20} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 uppercase font-bold">Enrolled</p>
                      <p className="font-semibold text-yellow">500+ Students</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center text-yellow-400">
                      <FaStar size={20} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 uppercase font-bold">Rating</p>
                      <p className="font-semibold text-yellow">4.8/5.0</p>
                    </div>
                  </div>
                </div>

                {/* Mobile-only CTA (Form is below on mobile, but this helps navigation) */}
                <div className="lg:hidden pt-4">
                   <button 
                     onClick={() => document.getElementById('enroll-form-card').scrollIntoView({ behavior: 'smooth' })}
                     className="w-full py-3 bg-blue-600 rounded-lg font-bold text-white shadow-lg"
                   >
                     Enroll Now
                   </button>
                </div>
              </motion.div>

              {/* Right Column: Lead Form (Sticky/Floating) */}
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="lg:col-span-5 relative"
              >
                <div id="enroll-form-card" className="bg-white shadow-2xl border border-slate-200 overflow-hidden relative z-20">
                  {/* Form Header */}
                  <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-center">
                    <h3 className="text-xl font-bold text-white">Book Your Free Demo</h3>
                    <p className="text-blue-100 text-sm mt-1">Get syllabus & course details instantly</p>
                  </div>
                  
                  {/* Form Body */}
                  <div className="p-6 md:p-8">
                    <form onSubmit={handleFormSubmit} className="space-y-4">
                      
                      {/* Course Name - Auto-filled and read-only */}
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5 ml-1">
                          Course Name
                        </label>
                        <input
                          type="text"
                          name="courseName"
                          value={courseData.title}
                          readOnly
                          className="w-full px-4 py-3 bg-slate-200 border border-slate-200 text-slate-600 font-semibold cursor-not-allowed focus:outline-none text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5 ml-1">Full Name</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleFormChange}
                          required
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-200 outline-none transition-all text-sm font-medium"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5 ml-1">Phone Number</label>
                        <div className="flex gap-3">
                          <select
                            name="countryCode"
                            value={formData.countryCode}
                            onChange={handleFormChange}
                            className="w-24 px-2 py-3 bg-slate-50 border border-slate-200 text-sm font-medium outline-none"
                          >
                            <option value="+91">🇮🇳 +91</option>
                            <option value="+1">🇺🇸 +1</option>
                            <option value="+44">🇬🇧 +44</option>
                          </select>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handlePhoneChange}
                            required
                            className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 outline-none transition-all text-sm font-medium"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5 ml-1">Email Address</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleFormChange}
                          required
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-200 outline-none transition-all text-sm font-medium"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold shadow-lg shadow-blue-500/30 transform active:scale-95 transition-all disabled:opacity-70 disabled:cursor-not-allowed mt-2"
                      >
                        {isSubmitting ? "Submitting..." : "Get Course Details"}
                      </button>

                      <p className="text-[10px] text-center text-slate-400">
                        We respect your privacy. No spam guaranteed.
                      </p>
                    </form>
                  </div>
                </div>
                
                {/* Decorative blob behind form */}
                <div className="absolute -bottom-6 -right-6 w-full h-full bg-blue-600/20 rounded-2xl blur-xl -z-10 transform rotate-3"></div>
              </motion.div>

            </div>
          </div>
        </section>

        {/* --- Sticky Navigation Bar --- */}
        <div className={`sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm transition-all duration-300 ${isScrolled ? 'py-0' : 'py-3'}`}>
          <div className="container mx-auto px-4 overflow-x-auto custom-scrollbar">
            <div className="flex items-center gap-8 min-w-max">
              {['Overview', 'Syllabus', 'Certification', 'Instructors', 'FAQ'].map((item) => {
                const key = item.toLowerCase();
                return (
                  <button
                    key={key}
                    onClick={() => scrollToSection(key)}
                    className={`py-3 text-sm font-bold border-b-2 transition-colors ${
                      activeTab === key 
                      ? "text-blue-600 border-blue-600" 
                      : "text-slate-500 border-transparent hover:text-slate-900"
                    }`}
                  >
                    {item}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* --- Main Content --- */}
        <div className="container mx-auto px-4 py-12 lg:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            {/* Left Content (Course Details) */}
            <div className="lg:col-span-8 space-y-16">
              
              {/* Overview */}
              <section id="overview" ref={sectionRefs.overview} className="scroll-mt-32">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <FaLaptopCode className="text-blue-600" /> Course Overview
                </h2>
                <div className="prose prose-slate max-w-none text-slate-600 leading-relaxed bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                  <p>{courseData.description || "This comprehensive course guides you through every step of the journey. From fundamentals to advanced implementation, you will learn by doing."}</p>
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4 mt-6">
                  {[
                    "100% Practical Training", "Industry Recognized Certificate",
                    "Placement Assistance", "Lifetime LMS Access"
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 p-4 bg-white border border-slate-100 rounded-xl shadow-sm">
                      <FaCheckCircle className="text-green-500 shrink-0" />
                      <span className="text-sm font-semibold text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </section>

              {/* Syllabus */}
              <section id="syllabus" ref={sectionRefs.syllabus} className="scroll-mt-32">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                   <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                     <FaChalkboardTeacher className="text-blue-600" /> Curriculum
                   </h2>
                   <button 
                     onClick={() => courseData.brochureUrl ? window.open(courseData.brochureUrl) : setIsPopupOpen(true)}
                     className="px-4 py-2 bg-blue-50 text-blue-600 text-sm font-bold rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-2"
                   >
                     <FaDownload /> Download Syllabus
                   </button>
                </div>

                <div className="space-y-4">
                  {syllabusData.map((module, idx) => (
                    <div key={idx} className="border border-slate-200 rounded-xl bg-white overflow-hidden transition-all hover:border-blue-200">
                      <button
                        onClick={() => toggleSyllabus(idx)}
                        className={`w-full flex items-center justify-between p-5 text-left ${activeSyllabus === idx ? 'bg-slate-50' : 'hover:bg-slate-50'}`}
                      >
                        <div className="flex items-center gap-4">
                           <span className="w-8 h-8 rounded-full bg-slate-800 text-white flex items-center justify-center text-sm font-bold">
                             {idx + 1}
                           </span>
                           <div>
                             <h3 className="font-bold text-slate-900">{module.title}</h3>
                             <p className="text-xs text-slate-500 mt-0.5">{module.description}</p>
                           </div>
                        </div>
                        <FaChevronDown className={`text-slate-400 transition-transform ${activeSyllabus === idx ? 'rotate-180' : ''}`} />
                      </button>
                      
                      <AnimatePresence>
                        {activeSyllabus === idx && (
                          <motion.div 
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                          >
                            <div className="p-5 pt-0 pl-[4.5rem] border-t border-slate-100 bg-slate-50/50">
                               <ul className="grid gap-2">
                                  {module.topics?.map((topic, tIdx) => (
                                    <li key={tIdx} className="text-sm text-slate-600 flex items-start gap-2">
                                      <span className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5 shrink-0"></span> {topic}
                                    </li>
                                  ))}
                               </ul>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              </section>

              {/* Certification */}
              <section id="certification" ref={sectionRefs.certification} className="scroll-mt-32">
                <div className="bg-gradient-to-r from-slate-900 to-blue-900 rounded-2xl p-8 text-white relative overflow-hidden shadow-xl">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
                  <div className="relative z-10 grid md:grid-cols-2 gap-8 items-center">
                    <div>
                      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2 text-yellow">
                        <FaCertificate className="text-yellow" /> Get Certified
                      </h2>
                      <p className="text-blue-100 mb-6 text-sm leading-relaxed">
                        Earn an industry-recognized certificate from Vidyastu upon completion. Validate your skills to employers worldwide.
                      </p>
                      <ul className="space-y-2 text-sm text-blue-100">
                         <li className="flex items-center gap-2"><FaCheckCircle className="text-emerald-400" /> LinkedIn Shareable</li>
                         <li className="flex items-center gap-2"><FaCheckCircle className="text-emerald-400" /> Lifetime Validity</li>
                      </ul>
                    </div>
                    <div className="relative transform rotate-2 hover:rotate-0 transition-all duration-500">
                      <img src="/images/cert-template.png" alt="Certificate" className="rounded-lg shadow-2xl border-4 border-white/10" />
                    </div>
                  </div>
                </div>
              </section>

              {/* Instructors */}
              <section id="instructors" ref={sectionRefs.instructors} className="scroll-mt-32">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Expert Instructors</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  {(courseData.facultyName?.includes("&") 
                     ? courseData.facultyName.split("&") 
                     : [courseData.facultyName || "Senior Faculty"]
                  ).map((name, idx) => (
                    <div key={idx} className="bg-white border border-slate-200 rounded-xl p-6 flex gap-4 hover:shadow-lg transition-shadow">
                       <div className="w-16 h-16 rounded-full bg-slate-100 overflow-hidden shrink-0 border border-slate-200">
                          <img 
                            src={courseData.facultyImages?.[idx] || courseData.facultyImage || "/images/faculty1.png"} 
                            alt={name} className="w-full h-full object-cover"
                            onError={(e) => e.target.src = "https://via.placeholder.com/150"}
                          />
                       </div>
                       <div>
                          <h3 className="font-bold text-slate-900">{name.trim()}</h3>
                          <p className="text-xs text-blue-600 uppercase font-bold mb-2">{courseData.facultyRoles?.[idx] || "Lead Instructor"}</p>
                          <p className="text-sm text-slate-500 line-clamp-3">
                             {courseData.facultyBios?.[idx] || "Experienced professional with deep industry knowledge and a passion for teaching."}
                          </p>
                       </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* FAQ */}
              <section id="faq" ref={sectionRefs.faq} className="scroll-mt-32">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Frequently Asked Questions</h2>
                <div className="space-y-4">
                   {(courseData.faqs || [
                      { q: "Is this course suitable for beginners?", a: "Yes, we start from the very basics." },
                      { q: "Do you offer placement support?", a: "Yes, we have a dedicated placement cell." },
                      { q: "What if I miss a live class?", a: "You will get access to the recording in your LMS." }
                   ]).map((faq, idx) => (
                      <div key={idx} className="bg-white border border-slate-200 rounded-xl">
                         <button
                           onClick={() => toggleFaq(idx)}
                           className="w-full flex items-center justify-between p-5 text-left font-semibold text-slate-800 hover:text-blue-600 transition-colors"
                         >
                            {faq.q || faq.question}
                            <FaChevronDown className={`text-slate-400 transition-transform ${activeFaq === idx ? 'rotate-180' : ''}`} />
                         </button>
                         <AnimatePresence>
                            {activeFaq === idx && (
                               <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                                  <div className="p-5 pt-0 text-slate-600 text-sm">{faq.a || faq.answer}</div>
                               </motion.div>
                            )}
                         </AnimatePresence>
                      </div>
                   ))}
                </div>
              </section>

            </div>

            {/* Right Sticky Sidebar (Desktop) */}
            <div className="hidden lg:block lg:col-span-4 relative">
              <div className="sticky top-24 space-y-6">
                
                {/* Contact Card */}
                <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6">
                   <h3 className="font-bold text-slate-900 mb-4">Need Help?</h3>
                   <div className="space-y-4">
                      <a href="tel:+917450010203" className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                         <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center"><FaPhone size={14}/></div>
                         <span className="text-sm font-semibold text-slate-700">+91 7450010203</span>
                      </a>
                      <a href="https://wa.me/917450010203" target="_blank" className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                         <div className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center"><IoLogoWhatsapp size={16}/></div>
                         <span className="text-sm font-semibold text-slate-700">Chat on WhatsApp</span>
                      </a>
                   </div>
                </div>

              </div>
            </div>

          </div>
        </div>

        {/* --- Mobile Bottom Sticky Bar --- */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 shadow-[0_-5px_20px_rgba(0,0,0,0.1)] z-50 flex gap-3">
           <a 
             href="tel:+917450010203"
             className="flex-1 py-3 bg-slate-100 text-slate-700 font-bold rounded-lg flex items-center justify-center gap-2 border border-slate-300"
           >
              <FaPhone /> Call
           </a>
           <button 
             onClick={() => document.getElementById('enroll-form-card').scrollIntoView({ behavior: 'smooth' })} 
             className="flex-[2] py-3 bg-blue-600 text-white font-bold rounded-lg flex items-center justify-center gap-2 shadow-lg"
           >
              Enroll Now
           </button>
        </div>
        
        {/* Syllabus Popup (Fallback) */}
        <SyllabusPopup
          isOpen={isPopupOpen}
          onClose={() => setIsPopupOpen(false)}
          courseTitle={courseData.title}
          syllabusLink={courseData.brochureUrl}
        />
        
      </div>
    </>
  );
};

export default CourseDetailTemplate;